import Image from "next/image";
import { formatDistanceToNow } from "date-fns";
import { Info, User } from "lucide-react";
import { ModalSectionLayout } from "./ModalSectionLayout";
import { ModalTitleInput } from "./ModalTitleInput";
import { CredentialStatusBadge } from "./CredentialStatusBadge";
import { NoxusFormLabel } from "../../Form/NoxusFormLabel";
import type { ActiveCredential } from "./types";

export function CredentialDetailsPanel({
  cred,
  groupId,
}: {
  cred: ActiveCredential;
  groupId: string;
}) {
  return (
    <ModalSectionLayout
      title="Details"
      description="General information about this connection."
    >
      <div className="space-y-5">
        <div>
          <NoxusFormLabel label="Title" />
          {cred.scope === "server" ? (
            <p className="text-sm">
              {cred.displayName} · Server-wide (managed by a platform
              administrator)
            </p>
          ) : (
            <ModalTitleInput
              credentialId={cred.credentialId}
              groupId={groupId}
              initial={cred.displayName}
            />
          )}
        </div>

        <div className="grid grid-cols-2 gap-x-5 gap-y-5 pt-1">
          <div>
            <NoxusFormLabel label="Connected by" />
            <p className="text-sm text-muted-foreground">
              {cred.connectedBy ?? "-"}
            </p>
          </div>
          <div>
            <NoxusFormLabel label="Connected at" />
            <p className="text-sm text-muted-foreground">
              {cred.connectedAt
                ? formatDistanceToNow(new Date(cred.connectedAt), {
                    addSuffix: true,
                  })
                : "-"}
            </p>
          </div>
          <div>
            <NoxusFormLabel label="Status" />
            <div className="flex items-center gap-1.5">
              <CredentialStatusBadge
                status={cred.status}
                validationError={
                  (cred.meta as Record<string, unknown> | null)
                    ?.validation_error != null
                    ? String(
                        (cred.meta as Record<string, unknown>).validation_error
                      )
                    : undefined
                }
              />
            </div>
          </div>
          <div>
            <NoxusFormLabel label="Type" />
            <p className="text-sm text-muted-foreground">
              {cred.provider.auth_type === "device_code"
                ? "Device sign-in"
                : cred.provider.auth_type !== "secret_auth"
                  ? "OAuth"
                  : "API Key / Secret"}
            </p>
          </div>
        </div>
      </div>
    </ModalSectionLayout>
  );
}
